import React, { useEffect, useMemo } from "react";
import { 
  COLUMNS_IDENTIFIERS, 
  COLORS, 
  ALLOWED_PURCHASE_STATUS, 
  DEFAULT_VALUES 
} from "../../components.constants";
import { SYSTEM_COLORS } from "../../system.colors";
import { 
  calculateEffectivePurchase, 
  getSupplierId,
  getPrefixId,
  getRowHighlightColors,
  getBaseValueForEffectivePurchase,
} from "../../../utils/utils";
import PropTypes from "prop-types";
import { textFormatter } from "../../../utils/formatter/formatter";


const setCalculateValueForSourceInput = (inputs, productId, row, localValue) => {
  const sourceField = inputs?.[productId]?.source ?? row?.original?.source;
  const packToUnitRatioField = inputs?.[productId]?.packToUnitRatio ?? row?.original?.packToUnitRatio;
  const effectiveValue = Number(localValue);

  if (sourceField) {
    const sourceOriginalValue = Number(row.original?.[sourceField]);

    if (sourceOriginalValue === effectiveValue) {
      const ratio = packToUnitRatioField === row.original.packToUnitRatio ? row.original.packToUnitRatio : packToUnitRatioField;
      
      return calculateEffectivePurchase(effectiveValue, ratio);
    }
  }

  return effectiveValue;
};

const DoubleArrowGreen = ({ width = 512, height = 512, color = COLORS.GREEN_ARROW, className = "" }) => {
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 512 512" 
      className={className}
      role="img"
      aria-label="Dupla seta para direita"
    >
      {/* Primeira seta */}
      <path 
        d="M20 70 
           C 12 70 5 77 5 85
           C 5 88 6 91 8 93
           L 138 223
           C 142 227 142 233 138 237
           L 8 367
           C 6 369 5 372 5 375
           C 5 383 12 390 20 390
           L 116 390
           C 120 390 123 388 125 385
           L 285 225
           C 293 217 293 203 285 195
           L 125 35
           C 123 32 120 30 116 30
           L 20 30
           C 12 30 5 37 5 45
           Z" 
        fill={color}
      />
      
      {/* Segunda seta */}
      <path 
        d="M240 70 
           C 232 70 225 77 225 85
           C 225 88 226 91 228 93
           L 358 223
           C 362 227 362 233 358 237
           L 228 367
           C 226 369 225 372 225 375
           C 225 383 232 390 240 390
           L 336 390
           C 340 390 343 388 345 385
           L 505 225
           C 513 217 513 203 505 195
           L 345 35
           C 343 32 340 30 336 30
           L 240 30
           C 232 30 225 37 225 45
           Z" 
        fill={color}
      />
    </svg>
  );
};

export const EffectivePurchaseCell = React.memo(({ row, showInputs, inputs, setInputs, suppliersAndProductRelations, isOrderHystoryPage = false }) => {
  const colName = COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE;
  const productId = row.original.productId;
  const storeId = row.original.storeId;

  const data = { inputs, productId, colName, row, isOrderHystoryPage, conditionValue: false }

  const [localValue, setLocalValue] = React.useState( getBaseValueForEffectivePurchase({ data }) );

  const [error, setError] = React.useState("");

  const highlightColors = getRowHighlightColors(row, inputs, isOrderHystoryPage);
  const color = highlightColors[colName] || "";

  const handleBlur = (e) => {
    const rawValue = Number(e.target.value);
    const maybeCalculated = setCalculateValueForSourceInput(inputs, productId, row, rawValue);
    const numericValue = Number(maybeCalculated);
    
    if (isNaN(numericValue) || numericValue <= 0) {
      setError("O valor deve ser maior que 0");
      return;
    }
    
    try {
      const resolvedSupplierId = getSupplierId(row, inputs, productId, suppliersAndProductRelations);
      
      const currentSource = inputs?.[productId]?.source;
      const shouldClearSource = currentSource && (() => {
        const sourceValue = inputs?.[productId]?.[currentSource];
        const packToUnitRatio = inputs?.[productId]?.packToUnitRatio || row.original.packToUnitRatio;
        const expectedValue = calculateEffectivePurchase(sourceValue, packToUnitRatio);
        return numericValue !== expectedValue;
      })();
      
      setError("");
      setLocalValue(numericValue);
      setInputs((prev) => ({
        ...prev,
        [productId]: {
          ...prev?.[productId],
          [colName]: numericValue,
          storeId: storeId ?? 0,
          supplierId: resolvedSupplierId,
          prefixId: getPrefixId(row, prev, productId),
          purchaseDate: row.original?.purchaseDate,
          purchaseRecommendationId: row.original.purchaseRecommendationId,
          purchaseStatus: isOrderHystoryPage
            ? ALLOWED_PURCHASE_STATUS.FINALIZED_EDITED
            : ALLOWED_PURCHASE_STATUS.EDITED,
          ...(shouldClearSource ? { source: undefined } : {}),
        },
      }));
    } catch (e) {
      setError("Selecione um fornecedor");
    }
  };

  const handleChange = (e) => setLocalValue(e.target.value);

  // useEffect(() => {
  //   const source = inputs?.[productId]?.source;
  //   const sourceValue = inputs?.[productId]?.[source];
    
  //   if (sourceValue === localValue) return 

  // }, [localValue, productId, inputs]);

  useEffect(() => {
    const newValue = getBaseValueForEffectivePurchase({ data });
    if (newValue !== undefined && newValue !== localValue) {
      setLocalValue(newValue);
    }
  }, [inputs, row.original, productId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      {showInputs ? (
        <>
          <input
            type="text"
            inputMode="numeric"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            pattern="[0-9]*"
            style={{ textAlign: "center", width: "64%", color: color }}
          />
          {error && <span style={{ color: "red", fontSize: "0.75rem" }}>{error}</span>}
        </>
      ) : (
        <span style={{ color: color }}>{String(localValue === undefined ? 0 : localValue)}</span>
      )}
    </div>
  );
});

export const PrefixNameCell = React.memo(({ 
  row, 
  showInputs, 
  inputs, 
  setInputs, 
  prefixes, 
  suppliersAndProductRelations, 
  isPrefixOfMeasurePage = false,
  isOrderHystoryPage = false
}) => {

  const productId = row.original.productId;
  const productData = row.original;
  const storeId = row.original.storeId;
  const prefixId = row.original.prefixId;

  const inputKey = isPrefixOfMeasurePage ? prefixId : productId;

  const initialPrefix = prefixes?.find((p) => p.prefixName === productData?.prefixName) || [];
  
  const [localValue, setLocalValue] = React.useState(inputs?.[inputKey]?.prefixId || initialPrefix?.prefixId || "");
  const [error, setError] = React.useState("");

  const handleChange = (e) => {
    const newPrefixId = Number(e.target.value);

    try {
      const resolvedSupplierId = getSupplierId(row, inputs, productId, suppliersAndProductRelations, isPrefixOfMeasurePage);

      setLocalValue(newPrefixId);
      setError("");

      setInputs((prev) => ({
        ...prev,
        [inputKey]: {
          ...prev?.[inputKey],
          prefixId: newPrefixId ?? 1,
          purchaseDate: row.original?.purchaseDate,
          purchaseRecommendationId: row.original.purchaseRecommendationId,
          storeId: storeId ?? 0,
          supplierId: resolvedSupplierId,           
          purchaseStatus: isOrderHystoryPage ? ALLOWED_PURCHASE_STATUS.FINALIZED_EDITED : ALLOWED_PURCHASE_STATUS.EDITED
        },
      }));
    } catch (e) {
      setError("Selecione um fornecedor");
    }
  };

  const prefixStyle = {
    textAlign: 'center',
    fontWeight: '700',
    color: 'white',
    backgroundColor: '#D1D5DB',
    padding: '0.4rem 0.6rem',
    borderRadius: '1rem',
    boxShadow: `0 2px 8px #D1D5DB33`
  };

  const prefixNameStyle = {
    display: "flex", 
    flexDirection: "column", 
    alignItems: "center", 
    justifyContent: "center"
  };

  React.useEffect(() => {
    const newValue = inputs?.[inputKey]?.prefixId ?? initialPrefix?.prefixId ?? "";

    if (newValue !== "" && newValue !== localValue) {
      setLocalValue(newValue);
    }
  }, [initialPrefix?.prefixId, inputs, inputKey, localValue]); 

  return (
    <div style={isPrefixOfMeasurePage ? {} : prefixNameStyle}>
      {showInputs ? (
        <>
          <select
            value={localValue}
            onChange={handleChange}
            style={{ textAlign: "center", width: "90%" }}
          >
              {Array.isArray(prefixes) &&
                prefixes
                  .filter(
                    (p) =>
                      typeof p?.prefixName === "string" &&
                      p.prefixName.trim() !== ""
                  )
                  .map((prefix) => (
                    <option key={prefix.prefixId} value={prefix.prefixId}>
                      {prefix.prefixName}
                    </option>
                  ))}

          </select>
          {error && (
            <span style={{ color: "red", fontSize: "0.75rem" }}>
              {error}
            </span>
          )}
        </>
      ) : (
        <span style={isPrefixOfMeasurePage ? {} : prefixStyle}>
          {Array.isArray(prefixes)
            ? prefixes.find((p) => p?.prefixId === localValue)?.prefixName ?? "UN"
            : "UN"}
        </span>
      )}
    </div>
  );
});

export const PrefixDescriptionCell = React.memo(({ row, showInputs, inputs, setInputs }) => {
  const colName = COLUMNS_IDENTIFIERS.PREFIX_DESCRIPTION;
  const prefixId = row.original.prefixId;
  const prefixData = row.original;

  const [localValue, setLocalValue] = React.useState(
    inputs?.[prefixId]?.prefixDescription ?? prefixData?.prefixDescription ?? ""
  );
  const [error, setError] = React.useState("");

  const MAX_LENGTH = 255;

  const handleBlur = () => {
    const newValue = localValue;

    if (newValue.length > MAX_LENGTH) {
      setError(`Máximo de ${MAX_LENGTH} caracteres excedido`);
      return;
    }

    if (newValue.length < 1 && newValue.length > 0) {
      setError("Mínimo de 1 caractere");
      return;
    }

    setError("");

    setInputs((prev) => ({
      ...prev,
      [prefixId]: {
        ...prev?.[prefixId],
        [colName]: newValue,
      },
    }));
  };

  return (
    <div style={{}}>
      {showInputs ? (
        <>
          <input
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            style={{ textAlign: "center", width: "90%" }}
            placeholder="Digite a descrição..."
          />
          {error && (
            <span style={{ color: "red", fontSize: "0.75rem" }}>
              {error}
            </span>
          )}
        </>
      ) : (
        <span>
          {localValue || "-"}
        </span>
      )}
    </div>
  );
});

export const PackToUnitRatioCell = React.memo(({ 
  row, 
  showInputs, 
  inputs, 
  setInputs, 
  suppliersAndProductRelations,
  isOrderHystoryPage = false,
 }) => {
  const colName = COLUMNS_IDENTIFIERS.PACK_TO_UNIT_RATIO;
  const productId = row.original.productId;
  const storeId = row.original.storeId;
  const productData = row.original;

  const [localValue, setLocalValue] = React.useState(
    inputs?.[productId]?.[colName] || productData?.packToUnitRatio || 1
  );
  const [error, setError] = React.useState("");

  const handleBlur = () => {
    const numericValue = Number(localValue);

    if (isNaN(numericValue) || numericValue <= 0) {
      setError("O valor deve ser maior que 0");
      return;
    }

    try {
      const resolvedSupplierId = getSupplierId(row, inputs, productId, suppliersAndProductRelations);

      setError("");

      setInputs((prev) => ({
        ...prev,
        [productId]: {
          ...prev?.[productId],
          [colName]: numericValue,
          storeId: storeId ?? 1,
          supplierId: resolvedSupplierId,
          purchaseRecommendationId: row.original.purchaseRecommendationId,
          prefixId: getPrefixId(row, inputs, productId),
          purchaseDate: row.original?.purchaseDate,        
          purchaseStatus: isOrderHystoryPage ? ALLOWED_PURCHASE_STATUS.FINALIZED_EDITED : ALLOWED_PURCHASE_STATUS.EDITED,
        },
      }));
    } catch (e) {
      setError("Selecione um fornecedor");
    }
  };

  React.useEffect(() => {
    const currentValue = inputs?.[productId]?.[colName] ?? productData?.packToUnitRatio ?? 1;

    if (currentValue !== undefined && !isNaN(currentValue) && currentValue !== localValue) {
      setLocalValue(currentValue);
    }
  }, [inputs, productData, productId, colName]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      {showInputs ? (
        <>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            style={{ textAlign: "center", width: "90%" }}
          />
          {error && (
            <span style={{ color: "red", fontSize: "0.75rem" }}>
              {error}
            </span>
          )}
        </>
      ) : (
        <span style={{ textAlign: "center" }}>{localValue}</span>
      )}
    </div>
  );
});

export const MinStockCell = React.memo(({ row, showInputs, inputs, setInputs, suppliersAndProductRelations }) => {
  const colName = COLUMNS_IDENTIFIERS.MIN_STOCK;
  const productId = row.original.productId;
  const productData = row.original;
  const storeId = row.original.storeId;

  const [localValue, setLocalValue] = React.useState(
    inputs?.[productId]?.[colName] ?? productData?.minStock ?? ""
  );
  const [error, setError] = React.useState("");

  const handleBlur = () => {
    const numericValue = Number(localValue);

    if (isNaN(numericValue) || numericValue <= 0) {
      setError("O valor deve ser maior que 0");
      return;
    }

    try {
      const resolvedSupplierId = getSupplierId(row, inputs, productId, suppliersAndProductRelations);

      setError("");

      setInputs((prev) => ({
        ...prev,
        [productId]: {
          ...prev?.[productId],
          [colName]: numericValue,   
          storeId: storeId ?? 0,          
          supplierId: resolvedSupplierId,
          purchaseRecommendationId: row.original.purchaseRecommendationId,
          prefixId: getPrefixId(row, inputs, productId),
          purchaseDate: row.original?.purchaseDate,          
          purchaseStatus: ALLOWED_PURCHASE_STATUS.EDITED,
        },
      }));
    } catch (e) {
      setError("Selecione um fornecedor");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      {showInputs ? (
        <>
          <input
            type="number"
            min={1}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            style={{ textAlign: "center", width: "80%" }}
          />
          {error && (
            <span style={{ color: "red", fontSize: "0.75rem" }}>
              {error}
            </span>
          )}
        </>
      ) : (
        <span>{localValue}</span>
      )}
    </div>
  );
});

export const LeadTimeCell = React.memo(({ row, showInputs, inputs, setInputs, suppliersAndProductRelations }) => {
  const colName = COLUMNS_IDENTIFIERS.LEAD_TIME;
  const productId = row.original.productId;
  const productData = row.original;
  const storeId = row.original.storeId;

  const [localValue, setLocalValue] = React.useState(
    inputs?.[productId]?.[colName] ?? productData?.leadTime ?? ""
  );
  const [error, setError] = React.useState("");

  const handleBlur = () => {
    const numericValue = Number(localValue);

    if (isNaN(numericValue) || numericValue <= 0) {
      setError("O valor deve ser maior que 0");
      return;
    }

    try {
      const resolvedSupplierId = getSupplierId(row, inputs, productId, suppliersAndProductRelations);

      setError("");

      setInputs((prev) => ({
        ...prev,
        [productId]: {
          ...prev?.[productId],
          [colName]: numericValue,
          storeId: storeId ?? 0,
          prefixId: getPrefixId(row, inputs, productId),
          purchaseRecommendationId: row.original.purchaseRecommendationId,
          purchaseDate: row.original?.purchaseDate,  
          supplierId: resolvedSupplierId,
          purchaseStatus: ALLOWED_PURCHASE_STATUS.EDITED,
        },
      }));
    } catch (e) {
      setError("Selecione um fornecedor");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      {showInputs ? (
        <>
          <input
            type="number"
            min={1}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            style={{ textAlign: "center", width: "64%" }}
          />
          {error && (
            <span style={{ color: "red", fontSize: "0.75rem" }}>
              {error}
            </span>
          )}
        </>
      ) : (
        <span>{localValue}</span>
      )}
    </div>
  );
});

export const SuppliersCell = React.memo(({ row, showInputs, inputs, setInputs, suppliersAndProductRelations, suppliersList }) => {
  const colName = COLUMNS_IDENTIFIERS.SUPPLIERS;
  const productId = row.original.productId;
  const storeId = row.original.storeId;

  const findSupplierObject = (supplierName) =>
    (suppliersList || suppliersAndProductRelations)
      .find(rel => rel.supplierName === supplierName);

  const editSuppliers = useMemo(() => {
    if (!Array.isArray(suppliersList) || suppliersList.length === 0)
      return [DEFAULT_VALUES.NOT_APPLY];
    return suppliersList
      .map(s => s.supplierName)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [suppliersList]);

  const savedSupplierName = row.original.suppliers?.[0]?.supplierName || DEFAULT_VALUES.NOT_APPLY;

  const firstMatch = inputs?.[productId]?.[colName] ?? savedSupplierName;

  const [localValue, setLocalValue] = React.useState(firstMatch);

  React.useEffect(() => {
    const newValue = inputs?.[productId]?.[colName];
    if (newValue && newValue !== localValue) {
      setLocalValue(newValue);
    }
  }, [inputs, productId, colName, localValue]);

  const currentSupplier = localValue === DEFAULT_VALUES.NOT_APPLY
    ? textFormatter(localValue, { textTransform: 'uppercase' })
    : textFormatter(localValue, { maxLength: 10 });

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
      {showInputs ? (
        <select
          value={localValue}
          onChange={(e) => {
            const selectedSupplierName = e.target.value;
            const supplierObject = findSupplierObject(selectedSupplierName);
            setLocalValue(selectedSupplierName);
            if (supplierObject) {
              setInputs(prev => ({
                ...prev,
                [productId]: {
                  ...prev?.[productId],
                  [colName]: selectedSupplierName,
                  storeId: storeId ?? 0,
                  prefixId: getPrefixId(row, inputs, productId),
                  purchaseRecommendationId: row.original.purchaseRecommendationId,
                  purchaseDate: row.original?.purchaseDate,
                  supplierId: supplierObject.supplierId,
                  purchaseStatus: ALLOWED_PURCHASE_STATUS.EDITED
                }
              }));
            }
          }}
          style={{
            width: "auto",
            maxWidth: "80px",
            minWidth: "60px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            textAlign: "center"
          }}
        >
          {editSuppliers.map((supplier, index) => (
            <option key={`${supplier}-${index}`} value={supplier}>
              {supplier}
            </option>
          ))}
        </select>
      ) : (
        <span
          style={{ color: COLORS.CELL_VALUE_COLOR, fontWeight: "bold" }}
          className="only-to-export-no-styles-added-suppliers"
          title={localValue}
        >
          {currentSupplier}
        </span>
      )}
    </div>
  );
});

export const ProjectedSalesCell = React.memo(({ getValue, row, columnId, showInputs, setInputs, suppliersAndProductRelations, inputs = {} }) => {
  const productId = row.original.productId;
  const storeId = row.original.storeId;
  const projectedValue = getValue();

  const [error, setError] = React.useState("");

  const highlightColors = React.useMemo(() => {
    return getRowHighlightColors(row, inputs);
  }, [row, inputs]);

  const color = highlightColors[columnId] || "";
  const hasMatch = !!color;

  const handleArrowClick = React.useCallback(() => {
    const numericValue = Number(projectedValue);

    if (isNaN(numericValue) || numericValue < 0) {
      setError("O valor deve ser maior que 0");
      return;
    }

    try {
      const resolvedSupplierId = getSupplierId(row, inputs, productId, suppliersAndProductRelations);
      const effectivePurchaseValue = calculateEffectivePurchase(numericValue, inputs?.[productId]?.packToUnitRatio || row.original.packToUnitRatio);
      setError("");

      setInputs(prev => ({
        ...prev,
        [productId]: {
          ...prev?.[productId],
          [COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE]: effectivePurchaseValue,
          [columnId]: numericValue,
          source: columnId,            
          prefixId: getPrefixId(row, inputs, productId),
          purchaseDate: row.original?.purchaseDate, 
          purchaseRecommendationId: row.original.purchaseRecommendationId,
          storeId: storeId ?? 0,
          supplierId: resolvedSupplierId, 
          purchaseStatus: ALLOWED_PURCHASE_STATUS.EDITED,
        }
      }));
    } catch (e) {
      setError("");
    }
  }, [projectedValue, productId, storeId, row, inputs, suppliersAndProductRelations, columnId, setInputs]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        color: color,
      }}
    >
      {!showInputs ? (
        <span>{projectedValue}</span>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: hasMatch ? 0 : "0.5rem", 
              width: "100%",
              justifyContent: hasMatch ? "center" : "center",
            }}
          >
            <span>{projectedValue}</span>

            {!hasMatch && (
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={handleArrowClick}
              >
                <DoubleArrowGreen width={10} height={10} />
              </button>
            )}
          </div>

          {error && (
            <span style={{ color: "red", fontSize: "0.75rem" }}>
              {error}
            </span>
          )}
        </>
      )}
    </div>
  );
});

const SEVERITY_PILL_COLORS = {
  red: {
    background: SYSTEM_COLORS.RED.MISTY_ROSE,
    border: SYSTEM_COLORS.RED.SALMON_PINK,
    dot: SYSTEM_COLORS.RED.CRIMSON,
    text: SYSTEM_COLORS.RED.FIREBRICK,
  },
  orange: {
    background: SYSTEM_COLORS.ORANGE.PEACH,
    border: SYSTEM_COLORS.ORANGE.APRICOT,
    dot: SYSTEM_COLORS.ORANGE.TANGERINE,
    text: SYSTEM_COLORS.ORANGE.RUST,
  },
  yellow: {
    background: SYSTEM_COLORS.YELLOW.PALE_YELLOW,
    border: SYSTEM_COLORS.YELLOW.LEMON,
    dot: SYSTEM_COLORS.YELLOW.AMBER,
    text: SYSTEM_COLORS.YELLOW.DARK_OCHRE,
  },
};

export const SeverityPillCell = React.memo(({ ctx, uiState }) => {

  const { value, severity } = ctx || {}
  const { type = undefined } = uiState || {}

  const colors = SEVERITY_PILL_COLORS[severity];

  const currencyFormatterNoDecimals = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const formattedValue = !!type ? value : currencyFormatterNoDecimals.format(Number(value));

  if (!colors) {
    return <CellValue value={formattedValue} />;
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '0.25rem 0.75rem',
        borderRadius: 999,
        background: colors.background,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        fontWeight: 700,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: colors.dot,
          flexShrink: 0,
        }}
      />

      {formattedValue}
    </span>
  );
});

export const CellValue = React.memo(({
  value,
  originalValue,
  className,
  padding = "",
  isTextCell = false,
  align = 'center',
  styles = {}
}) => {
  if (isTextCell) {
    return (
      <span 
        style={{ 
          color: COLORS.CELL_VALUE_COLOR, 
          fontWeight: "bold", 
          padding: padding
        }} 
        className={className}
        title={originalValue || value}>
        {value}
      </span>
    );
  }

  const defaultCellStyle = {
    width: '100%',  
    display: 'flex', 
    justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', 
    alignItems: 'center',
  };

  return (
    <span style={{ ...defaultCellStyle, ...styles }} title={originalValue}>
      {value}
    </span>
  );
});

export const SelectCell = React.memo(({ actions, ctx, uiState }) => {
  const { 
    data = {}, 
    isPrefixOfMeasurePage = false
  } = ctx || {};
  const { 
    setCheckedProducts = () => {}, 
  } = actions || {};
  const { 
    showInputs = false,
    checkedProducts,
  } = uiState || {};

  if (!showInputs) return null;

  const { productId, prefixId } = data;
  const isChecked = isPrefixOfMeasurePage ? checkedProducts?.includes(prefixId) : checkedProducts?.includes(productId);

  const handleToggle = () => {
    const id = isPrefixOfMeasurePage ? prefixId : productId;
    
    if (isChecked) {
      setCheckedProducts(prev => prev.filter(checkId => checkId !== id));
    } else {
      setCheckedProducts(prev => [...prev, id]);
    }
  };

  return (
    <td style={{ textAlign: 'center', padding: '0.5rem' }}>
      <input
        type="checkbox"
        checked={isChecked}
        onChange={handleToggle}
        style={{
          width: '18px',
          height: '18px',
          cursor: 'pointer',
          accentColor: '#dc2626'
        }}
        title={isChecked ? 'Desmarcar produto' : 'Selecionar produto'}
      />
    </td>
  );
});

export const InformationForTheUser = React.memo(({ actions, ctx }) => {
  const { closeModal } = actions || {};
  const { intro, items } = ctx || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px' }}>
      
      {intro && <p style={{ margin: 0 }}>{intro}</p>}

      {items?.map((item, index) => (
        <p
          key={index}
          style={{
            margin: 0,
            ...(item.italic ? { color: '#5f5e5e', fontStyle: 'italic' } : {})
          }}
        >
          {item.label && <strong>{item.label} </strong>}
          {item.text}
        </p>
      ))}

      <button
        onClick={closeModal}
        style={{
          marginTop: '0.5rem',
          padding: '0.5rem 1.25rem',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: '#f26f21',
          color: '#fff',
          fontWeight: 500,
          cursor: 'pointer',
          alignSelf: 'center'
        }}
      >
        Clique para fechar
      </button>
    </div>
  );
});

InformationForTheUser.propTypes = {};

SeverityPillCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  severity: PropTypes.oneOf(['red', 'orange', 'yellow']),
};

CellValue.propTypes = {
  getValue: PropTypes.func,
  row: PropTypes.shape(), 
  dataKey: PropTypes.string, 
  textTransform: PropTypes.string,
  className: PropTypes.string,
  maxLength: PropTypes.number,
  isTextCell: PropTypes.bool, 
  padding: PropTypes.string
};

LeadTimeCell.propTypes = {
  row: PropTypes.shape(),
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func,
  saveLoading: PropTypes.bool
};

MinStockCell.propTypes = {
  row: PropTypes.shape(),
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func,
  saveLoading: PropTypes.bool  
};

PrefixNameCell.propTypes = {
  row: PropTypes.shape(),
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func,
  saveLoading: PropTypes.bool  
};

PackToUnitRatioCell.propTypes = {
  row: PropTypes.shape(),
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func,
  saveLoading: PropTypes.bool  
};

EffectivePurchaseCell.propTypes = {
  row: PropTypes.shape(),
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func,
  saveLoading: PropTypes.bool  
};

SuppliersCell.propTypes = {
  row: PropTypes.shape(),
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func,
  saveLoading: PropTypes.bool,
};

ProjectedSalesCell.propTypes = {
  getValue: PropTypes.func,
  row: PropTypes.shape(),
  columnId: PropTypes.string, 
  showInputs: PropTypes.bool,
  inputs: PropTypes.shape(),
  setInputs: PropTypes.func, 
};

DoubleArrowGreen.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string
};